<template>
  <NuxtLayout>
    <div class="text-purple-950">
      <AppHeader
        :mode="mode"
        @update:mode="onModeChange"
        @search-click="onHeaderSearchClick"
        @menu-click="onHeaderMenuClick"
        @login="onHeaderLogin"
        @how-it-works-click="onHowItWorks"
      />
      <div
        class="mx-auto w-full max-w-[1366px] px-4 pt-4 sm:px-5 md:px-[15vw] lg:px-[12vw] xl:px-[12vw]"
      >
        <button class="mb-2 flex flex-row items-center gap-1" @click="onBack">
          <MdiIcon :size="'1.5rem'" icon="mdiChevronLeft" /><span>{{
            $t("event.back")
          }}</span>
        </button>
      </div>
      <div
        class="mx-auto flex w-full max-w-[1366px] flex-1 flex-col items-start px-4 sm:px-5 md:px-[15vw] lg:px-[12vw] xl:px-[12vw]"
      >
        <div
          v-if="eventError"
          class="mb-4 w-full rounded bg-red-50 p-4 text-red-700"
        >
          {{ $t("event.failedToLoad") }}
        </div>
        <div
          v-if="paymentStatusMessage"
          :class="[
            'mb-4 w-full rounded p-4 text-sm',
            paymentStatusKind === 'success'
              ? 'border border-green-100 bg-green-50 text-green-700'
              : '',
            paymentStatusKind === 'canceled'
              ? 'border border-amber-100 bg-amber-50 text-amber-800'
              : '',
            paymentStatusKind === 'availability'
              ? 'border border-amber-100 bg-amber-50 text-amber-800'
              : '',
          ]"
        >
          {{ paymentStatusMessage }}
        </div>

        <div
          class="mb-3 flex w-full flex-1 flex-col items-start justify-between gap-3 sm:flex-row sm:items-center"
        >
          <h1 class="my-1 flex flex-col sm:my-3">
            <span class="font-rounded text-2xl font-extrabold">{{
              eventName
            }}</span>
            <NuxtLink
              :to="localePath(`/establishment/${estPath}`)"
              class="text-xs underline underline-offset-2 hover:text-pink-500"
              >{{
                $t("event.by", {
                  name: establishmentName.toUpperCase(),
                })
              }}</NuxtLink
            >
          </h1>
          <button
            class="flex items-center gap-1.5 rounded-full border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 hover:text-[#2e234b]"
            @click="isShareModalOpen = true"
          >
            <svg
              class="h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
              />
            </svg>
            {{ $t("share.title") }}
          </button>
        </div>
        <ImageGallery
          :images="imagesList"
          :alt-prefix="eventName"
          fallback-image="/images/placeholder-soft-neutral-event.svg"
        />

        <!-- 2-column layout starts after gallery: content + right sidebar ad -->
        <div class="mt-4 flex w-full gap-10">
          <!-- Left: main event content -->
          <div class="min-w-0 flex-1">
            <div
              v-if="isMultiDay"
              class="flex w-full flex-col gap-2 sm:flex-row sm:justify-between"
            >
              <div class="flex flex-row items-center gap-2">
                <MdiIcon icon="mdiCalendarOutline" />
                <p>
                  <span class="font-semibold">{{ $t("event.start") }}:</span>
                  {{ displayFormattedStartDateTime }}
                </p>
              </div>
              <div class="flex flex-row items-center gap-2">
                <MdiIcon icon="mdiCalendarOutline" />
                <p>
                  <span class="font-semibold">{{ $t("event.end") }}:</span>
                  {{ displayFormattedEndDateTime }}
                </p>
              </div>
              <div
                v-if="ticketTypes.length > 0"
                class="flex flex-row items-center gap-2"
              >
                <p class="text-pink-500">
                  {{
                    lowestPrice > 0
                      ? $t("event.fromPrice", {
                          price: formatLocalizedPrice(lowestPrice, currency),
                        })
                      : $t("event.free")
                  }}
                </p>
              </div>
            </div>
            <div
              v-else
              class="flex w-full flex-col gap-2 sm:flex-row sm:justify-between sm:gap-4"
            >
              <div class="flex flex-row items-center gap-2">
                <MdiIcon icon="mdiCalendarOutline" />
                <p>{{ displayFormattedSingleDate }}</p>
              </div>
              <div class="flex flex-row items-center gap-2">
                <MdiIcon icon="mdiClockOutline" />
                <p>{{ displayTimeRange }}</p>
              </div>
              <div
                v-if="ticketTypes.length > 0"
                class="flex flex-row items-center gap-2"
              >
                <p class="text-pink-500">
                  {{
                    lowestPrice > 0
                      ? $t("event.fromPrice", {
                          price: formatLocalizedPrice(lowestPrice, currency),
                        })
                      : $t("event.free")
                  }}
                </p>
              </div>
            </div>
            <p class="mt-2 text-xs text-slate-500">{{ timeZoneHint }}</p>

            <!-- Event Status Badge -->
            <div
              v-if="
                (eventStatus !== 'upcoming' && eventStatus !== 'invalid') ||
                hasAgeRestriction
              "
              class="mt-3 flex w-full flex-wrap gap-2"
            >
              <span
                v-if="hasAgeRestriction"
                class="inline-flex items-center gap-1.5 rounded-full bg-orange-100 px-3 py-1 text-xs font-semibold text-orange-700"
              >
                <span
                  class="inline-block h-2 w-2 rounded-full bg-orange-500"
                ></span>
                {{ $t("event.ageRestriction", { age: evt?.minimumAge }) }}
              </span>
              <span
                v-if="eventStatus === 'canceled'"
                class="inline-flex items-center gap-1.5 rounded-full bg-red-100 px-3 py-1 text-xs font-semibold text-red-700"
              >
                <span
                  class="inline-block h-2 w-2 rounded-full bg-red-500"
                ></span>
                {{ $t("event.canceled") }}
              </span>
              <span
                v-else-if="eventStatus === 'past'"
                class="inline-flex items-center gap-1.5 rounded-full bg-gray-200 px-3 py-1 text-xs font-semibold text-gray-600"
              >
                <span
                  class="inline-block h-2 w-2 rounded-full bg-gray-400"
                ></span>
                {{ $t("event.ended") }}
              </span>
              <span
                v-else-if="eventStatus === 'in-progress'"
                class="inline-flex items-center gap-1.5 rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-700"
              >
                <span
                  class="inline-block h-2 w-2 animate-pulse rounded-full bg-green-500"
                ></span>
                {{ $t("event.inProgress") }}
              </span>
              <span
                v-else-if="eventStatus === 'starting-soon'"
                class="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700"
              >
                <span
                  class="inline-block h-2 w-2 animate-pulse rounded-full bg-amber-500"
                ></span>
                {{ $t("event.startingSoon") }}
              </span>
            </div>

            <div
              class="mt-4 flex w-full flex-col gap-3 sm:flex-row sm:items-center"
            >
              <div
                v-if="announcementCount > 0"
                class="flex items-center sm:mr-auto"
              >
                <button
                  class="relative inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition-colors"
                  :class="
                    showAnnouncements
                      ? 'bg-purple-100 text-purple-800'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  "
                  @click="showAnnouncements = !showAnnouncements"
                >
                  <svg
                    class="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                    />
                  </svg>
                  Announcements
                  <span
                    class="inline-flex h-5 w-5 items-center justify-center rounded-full bg-pink-500 text-xs font-bold text-white"
                  >
                    {{ announcementCount }}
                  </span>
                </button>
              </div>
              <div v-if="canPurchase" class="w-full sm:ml-auto sm:w-auto">
                <GradientButton
                  class="w-full sm:hidden"
                  width="100%"
                  :text="$t('event.buyTicket')"
                  @click="onBuyTicketNow"
                />
                <GradientButton
                  class="hidden sm:inline-flex"
                  :text="$t('event.buyTicket')"
                  @click="onBuyTicketNow"
                />
              </div>
              <div v-else class="flex flex-col gap-1 sm:ml-auto sm:items-end">
                <button
                  disabled
                  class="w-full cursor-not-allowed rounded-full bg-gray-200 px-6 py-2 text-sm font-semibold tracking-wide text-gray-400 sm:w-auto"
                >
                  {{ $t("event.buyTicket") }}
                </button>
                <span class="text-xs text-gray-400">
                  {{
                    eventStatus === "canceled"
                      ? $t("event.eventCanceled")
                      : isEventSoldOut
                        ? $t("event.allSoldOut")
                        : !isEstablishmentEnabled
                          ? $t("event.notAcceptingSales")
                          : $t("event.salesClosed")
                  }}
                </span>
              </div>
            </div>

            <!-- Announcements Panel -->
            <div
              v-if="showAnnouncements && announcementCount > 0"
              class="mt-4 w-full rounded-2xl border border-purple-100 bg-purple-50 p-6"
            >
              <h3 class="mb-4 text-lg font-bold text-purple-900">
                {{ $t("event.announcements") }}
              </h3>
              <div class="max-h-80 space-y-4 overflow-y-auto">
                <div
                  v-for="ann in announcements"
                  :key="ann.id"
                  class="rounded-xl border border-purple-50 bg-white p-4 shadow-sm"
                >
                  <div class="flex items-start justify-between gap-2">
                    <h4 class="text-sm font-semibold text-[#2e234b]">
                      {{ ann.title }}
                    </h4>
                    <span class="whitespace-nowrap text-xs text-gray-400">{{
                      formatAnnouncementDate(ann.createdAt)
                    }}</span>
                  </div>
                  <p class="mt-1 text-sm text-gray-600">{{ ann.body }}</p>
                </div>
              </div>
            </div>

            <div v-if="categoriesList.length > 0" class="mt-4 w-full">
              <h3 class="mb-2 text-sm font-semibold">
                {{ $t("event.categories") }}
              </h3>
              <div class="flex flex-wrap items-center gap-2">
                <div
                  v-for="cat in categoriesList"
                  :key="cat.id"
                  class="inline-flex items-center gap-1.5 rounded-full border border-purple-100 bg-purple-50 px-3 py-1.5"
                >
                  <Icon
                    class="h-3.5 w-3.5 flex-shrink-0 text-purple-950"
                    :name="resolveCategoryIconName(cat)"
                  />
                  <span
                    class="whitespace-nowrap text-xs font-medium text-purple-950"
                    :title="translateCategory(cat.name)"
                    >{{ translateCategory(cat.name) }}</span
                  >
                </div>
              </div>
            </div>

            <h2 class="mt-4 font-rounded font-bold">
              {{ eventName.toUpperCase() }}
            </h2>
            <p class="mt-2 whitespace-pre-line">{{ eventDescription }}</p>
          </div>
          <!-- /main event content column -->

          <!-- Right Sidebar Ad (xl+ only) — starts below gallery, sticky while scrolling
                  <aside class="hidden xl:flex flex-col items-center w-[160px] flex-shrink-0 sticky top-4 self-start" aria-label="Advertisement" role="complementary">
                    <span class="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Ad</span>
                    <div class="w-[160px]">
                      <ClientOnly><AdsAdUnit slot-id="EVENT_SIDEBAR_SLOT_ID" format="vertical" :responsive="false" /></ClientOnly>
                    </div>
                  </aside> -->
        </div>
        <!-- /2-column layout -->

        <!-- Tickets and Services - Responsive Layout -->
        <div class="mt-6 flex w-full flex-col gap-6 lg:flex-row">
          <!-- Available Tickets Card -->
          <div class="flex-1 rounded-2xl bg-gray-100 p-6 md:p-8">
            <h3
              class="mb-6 text-center text-lg font-bold tracking-wide md:text-xl"
            >
              {{ $t("event.availableTickets") }}
            </h3>
            <div
              v-if="ticketTypes.length > 0"
              class="max-h-64 space-y-3 overflow-y-auto overflow-x-hidden pr-1"
            >
              <div
                v-for="ticket in ticketTypes"
                :key="ticket.id"
                class="option-row flex items-start gap-4"
                :class="[
                  { 'opacity-50': isTicketTypeSoldOut(ticket) },
                  canPurchase && !isTicketTypeSoldOut(ticket)
                    ? 'option-row--interactive'
                    : '',
                ]"
                @click="onTicketTypeClick(ticket)"
              >
                <div
                  class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#2e234b]"
                >
                  <svg
                    class="h-6 w-6 text-white"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      d="M2 6a2 2 0 012-2h12a2 2 0 012 2v2a2 2 0 100 4v2a2 2 0 01-2 2H4a2 2 0 01-2-2v-2a2 2 0 100-4V6z"
                    />
                  </svg>
                </div>
                <div class="flex-1">
                  <div class="text-sm font-bold text-[#2e234b] md:text-base">
                    {{ ticket.name ?? ticket.ticketName ?? "Ticket" }}
                  </div>
                  <div class="text-xs text-gray-600 md:text-sm">
                    {{ ticket.description ?? "" }}
                  </div>
                  <span
                    v-if="isTicketTypeSoldOut(ticket)"
                    class="mt-1 inline-block rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-600"
                  >
                    {{ $t("event.soldOut") }}
                  </span>
                  <span
                    v-else-if="
                      ticketTypeRemaining(ticket) !== null &&
                      ticketTypeRemaining(ticket)! <= 10
                    "
                    class="mt-1 inline-block rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700"
                  >
                    {{
                      $t("event.left", { count: ticketTypeRemaining(ticket) })
                    }}
                  </span>
                </div>
                <div class="text-base font-bold text-[#2e234b] md:text-lg">
                  {{
                    formatLocalizedPrice(
                      Number(ticket.price ?? 0),
                      ticket.currency ?? currency,
                    )
                  }}
                </div>
              </div>
            </div>
            <div v-else class="text-center text-gray-500">
              <p>{{ $t("event.noTicketsAvailable") }}</p>
            </div>
          </div>

          <!-- Service Options Card -->
          <div
            v-if="eventServices.length > 0"
            class="flex-1 rounded-2xl bg-gray-100 p-6 md:p-8"
          >
            <h3
              class="mb-6 text-center text-lg font-bold tracking-wide md:text-xl"
            >
              {{ $t("event.bookServiceOption") }}
            </h3>
            <div
              v-if="eventServices.length > 0"
              class="max-h-64 space-y-3 overflow-y-auto overflow-x-hidden pr-1"
            >
              <div
                v-for="svc in eventServices"
                :key="svc.id"
                class="option-row flex items-start gap-4"
                :class="
                  canPurchase && !isServiceOptionSoldOut(svc)
                    ? 'option-row--interactive'
                    : ''
                "
                @click="onServiceOptionClick(svc)"
              >
                <div class="flex flex-1 flex-row gap-4">
                  <div class="text-sm font-bold text-[#2e234b] md:text-base">
                    {{ svc.name }}
                  </div>
                  <div class="text-xs text-gray-600 md:text-sm">
                    {{ svc.description ?? "" }}
                  </div>
                  <span
                    v-if="
                      (svc.maxQuantity ?? 0) > 0 && isServiceOptionSoldOut(svc)
                    "
                    class="text-xs font-semibold text-red-500"
                    >{{ $t("event.soldOut") }}</span
                  >
                  <span
                    v-else-if="(svc.maxQuantity ?? 0) > 0"
                    class="text-xs font-semibold text-gray-500"
                    >{{
                      $t("event.left", {
                        count: availableQuantity(svc),
                      })
                    }}</span
                  >
                </div>
                <div class="text-base font-bold text-[#2e234b] md:text-lg">
                  {{
                    formatLocalizedPrice(
                      Number(svc.price ?? 0),
                      svc.currency ?? currency,
                    )
                  }}
                </div>
              </div>
            </div>
            <div v-else class="text-center text-gray-500">
              <p>{{ $t("event.noServiceOptions") }}</p>
            </div>
          </div>
        </div>
        <!-- Benefits and Restrictions -->
        <div
          v-if="hasAgeRestriction"
          class="mt-4 flex w-full items-start gap-2 rounded-lg border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-800"
        >
          <svg
            class="mt-0.5 h-4 w-4 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span>{{
            $t("event.ageRestrictionNotice", { age: evt?.minimumAge })
          }}</span>
        </div>

        <!-- Ad Slot: inline leaderboard after tickets and services -->
        <div
          class="mb-2 mt-8 w-full"
          aria-label="Advertisement"
          role="complementary"
        >
          <!-- <span class="text-[10px] uppercase tracking-wider text-gray-400"
            >Sponsored</span
          > -->
          <!-- <div class="mt-1">
            <ClientOnly>
              <AdsAdUnit slot-id="EVENT_INLINE_MID_SLOT_ID" />
            </ClientOnly>
          </div> -->
        </div>
      </div>

      <section
        class="mx-auto w-full max-w-[1366px] px-4 pb-12 sm:px-5 md:px-[15vw] lg:px-[12vw] xl:px-[12vw]"
      >
        <h2 class="my-6 text-xl font-bold">{{ $t("event.locationTitle") }}</h2>
        <div v-if="formattedAddressLines.length" class="mb-4 space-y-0">
          <p
            v-for="(line, idx) in formattedAddressLines"
            :key="idx"
            class="text-base"
          >
            {{ line }}
          </p>
        </div>
        <ClientOnly>
          <div class="mt-4">
            <LeafletMap
              v-if="lat !== null && lon !== null"
              :lat="lat"
              :lon="lon"
              :aria-label="`Map showing location of ${eventName}`"
            />
            <div v-else class="text-sm text-gray-500">
              {{ $t("event.locationNotAvailable") }}
            </div>
          </div>
        </ClientOnly>
      </section>

      <ShareModal
        v-model:show="isShareModalOpen"
        :title="eventName"
        :description="eventDescription"
        :image-url="ogImage"
        :url="canonicalUrl"
        :date="eventScheduleLabel"
        :location="shareLocation"
        :price="sharePrice"
      />

      <TicketPurchaseModal
        v-model:show="isTicketModalOpen"
        :event-name="eventName"
        :ticket-types="ticketTypes"
        :service-options="eventServices"
        :currency="currency"
        :persist-key="selectionStorageKey"
        :seller-name="sellerName"
        :event-id="evt?.id"
        :visibility="evt?.visibility"
        :private-access-mode="evt?.privateAccessMode"
        :age-restriction-enabled="evt?.ageRestrictionEnabled"
        :minimum-age="evt?.minimumAge"
        @close="closeTicketModal"
        @login="startLoginFlow"
        @pay="handlePayFromModal"
      />
    </div>
  </NuxtLayout>
</template>

<script setup lang="ts">
import { checkoutAnalyticsProperties } from "~/utils/productAnalytics";
import { ref, computed, watchEffect, watch, onMounted } from "vue";
import AppHeader from "~/components/header/AppHeader.vue";
import ImageGallery from "~/components/image-gallery/ImageGallery.vue";
import GradientButton from "~/components/buttons/GradientButton.vue";
import LeafletMap from "~/components/map/LeafletMap.vue";
import Icon from "~/components/Icon.vue";
import TicketPurchaseModal from "~/components/TicketPurchaseModal.vue";
import ShareModal from "~/components/share/ShareModal.vue";
import type { PurchasePayload } from "~/components/ticket-purchase/types";
import {
  availableQuantity,
  isUnavailable,
} from "~/components/ticket-purchase/availability";
import { ApiClient, type ApiClientOptions } from "~~/shared/api/ApiClient";
import { useApiBaseUrl } from "~~/shared/models";
import { EventsService } from "~~/shared/services/events";
import { AddressService } from "~~/shared/services/address";
import { useAnalytics } from "~/composables/useAnalytics";
import { extractUuidFromSlug, makeSlug } from "~~/shared/utils/slugUtils";
import { formatPrice } from "~~/shared/utils/formatPrice";
import type { EventsCategoryResponse } from "~~/shared/models/events";
import type { PublicEventDetailResponse } from "~~/shared/models/public-detail";
import type { TicketOfferInput } from "~~/shared/utils/seo/entitySchemas";
import {
  buildBreadcrumbSchema,
  buildEventSchema,
  getLowestTicketOffer,
} from "~~/shared/utils/seo/entitySchemas";
import {
  eventCalendarDateKey,
  formatEventDateInTimeZone,
  resolveEventTimeZoneCandidate,
  toEventDate,
} from "~~/shared/utils/seo/eventTime";
import { jsonLdHeadScript } from "~~/shared/utils/seo/jsonLd";
import {
  fingerprintAccessToken,
  isPublicVenueDetail,
} from "~~/shared/utils/entityAccess";
import { setResponseHeader } from "h3";

const { trackOpened, emit } = useAnalytics();
const { translateCategory } = useCategoryTranslation();

type Mode = "event" | "establishment";

const opts: ApiClientOptions = {
  baseUrl: useApiBaseUrl(),
};
const client = new ApiClient(opts);

const eventService = new EventsService(client);
const addressService = new AddressService(client);

const route = useRoute();
const router = useRouter();
const localePath = useLocalePath();
const serverRequestEvent = import.meta.server ? useRequestEvent() : undefined;
const { APP_URL } = usePublicUrls();
const {
  fallbackImagePath: socialFallbackImage,
  normalizeMetaText,
  toAbsolutePublicUrl,
} = useSocialSeo();
const SOCIAL_FALLBACK_IMAGE = socialFallbackImage;

const { locale, t, tm } = useI18n();
const categoryNameMessages = computed(() => {
  return (tm("categoryNames") as Record<string, string>) || {};
});

const translatedCategoryToKey = computed(() => {
  const reverse: Record<string, string> = {};
  for (const [key, value] of Object.entries(categoryNameMessages.value)) {
    if (typeof value !== "string") continue;
    const v = value.trim();
    if (v) reverse[v] = key;
  }
  return reverse;
});

function normalizeCategoryToken(v: unknown): string {
  if (typeof v !== "string") return "";
  return v
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
}

type CategoryLike = {
  iconName?: string | null;
  icon_name?: string | null;
  icon?: string | null;
  name?: string | null;
  categoryName?: string | null;
};

function resolveCategoryIconName(cat: CategoryLike | null | undefined): string {
  const candidates = [
    cat?.iconName,
    cat?.icon_name,
    cat?.icon,
    cat?.name,
    cat?.categoryName,
    translateCategory(cat?.name ?? ""),
  ].filter(
    (x) => typeof x === "string" && String(x).trim().length > 0,
  ) as string[];

  const map = categoryNameMessages.value;
  const reverse = translatedCategoryToKey.value;
  const keyByNormalized = new Map<string, string>();

  for (const key of Object.keys(map)) {
    keyByNormalized.set(normalizeCategoryToken(key), key);
  }
  for (const [translated, key] of Object.entries(reverse)) {
    keyByNormalized.set(normalizeCategoryToken(translated), key);
  }

  for (const value of candidates) {
    if (map[value]) return value;
    if (reverse[value]) return reverse[value];
    const normalized = normalizeCategoryToken(value);
    const byNorm = keyByNormalized.get(normalized);
    if (byNorm) return byNorm;
  }

  if (typeof cat?.iconName === "string") return cat.iconName;
  if (typeof cat?.name === "string") return cat.name;
  return "";
}

const paymentStatusKind = computed(() => {
  if (route.query.availabilityConflict === "1") return "availability";
  const status = String(route.query.payStatus ?? "").toLowerCase();
  if (status === "success") return "success";
  if (status === "canceled") return "canceled";
  return "";
});

const paymentStatusMessage = computed(() => {
  if (paymentStatusKind.value === "success") {
    return t("event.paymentSuccessful");
  }
  if (paymentStatusKind.value === "canceled") {
    return t("event.checkoutCanceled");
  }
  if (paymentStatusKind.value === "availability") {
    return t("event.availabilityRefreshed");
  }
  return "";
});

const placeholderImages = ["/images/placeholder-soft-neutral-event.svg"];

// Ticket flow state (handled inside TicketPurchaseModal)
const isTicketModalOpen = ref(false);
const selectionStorageKey = "upfest-ticket-flow";

// Header handlers
const mode = ref<Mode>("event");
function onModeChange(_next: Mode) {
  // AppHeader handles navigation automatically
}
function onHeaderSearchClick() {}
function onHeaderMenuClick() {}
function onHeaderLogin() {
  window.location.href = `${APP_URL}/`;
}
function onHowItWorks() {}

function onBack() {
  if (import.meta.client && window.history.length > 1) {
    router.back();
  } else {
    // fallback to parent establishment page
    const estSlug = String(route.params.establishmentSlug ?? "");
    router.push(localePath(`/establishment/${estSlug}`));
  }
}

function throwEventNotFound(): never {
  throw createError({
    statusCode: 404,
    statusMessage: "Event not found",
    fatal: true,
  });
}

function throwPublicBffError(error: unknown, resource: "Event"): never {
  const source = error as {
    status?: unknown;
    statusCode?: unknown;
    response?: { status?: unknown };
    data?: { statusCode?: unknown };
  };
  const upstreamStatus = Number(
    source?.statusCode ??
      source?.status ??
      source?.response?.status ??
      source?.data?.statusCode,
  );
  const statusCode =
    upstreamStatus === 404 || upstreamStatus === 410 ? upstreamStatus : 503;
  if (statusCode === 503) setNoStoreResponseHeader();
  throw createError({
    statusCode,
    statusMessage:
      statusCode === 410
        ? `${resource} permanently removed`
        : statusCode === 404
          ? `${resource} not found`
          : "Service unavailable",
    fatal: true,
  });
}

function setNoStoreResponseHeader() {
  if (serverRequestEvent) {
    setResponseHeader(serverRequestEvent, "Cache-Control", "no-store");
  }
}

function setPrivateResponseHeaders() {
  if (!serverRequestEvent) return;
  setResponseHeader(serverRequestEvent, "Cache-Control", "private, no-store");
  setResponseHeader(serverRequestEvent, "X-Robots-Tag", "noindex,nofollow");
}

// Reject malformed event and parent identifiers before making any API request.
const routeEventSlug = String(route.params.eventSlug ?? "");
const routeEstablishmentSlug = String(route.params.establishmentSlug ?? "");
const resolvedEventId = extractUuidFromSlug(routeEventSlug);
const resolvedRouteEstablishmentId = extractUuidFromSlug(
  routeEstablishmentSlug,
);
if (!resolvedEventId || !resolvedRouteEstablishmentId) {
  throw createError({
    statusCode: 404,
    statusMessage: "Event not found",
    fatal: true,
  });
}
const eventRenderNow = useHydrationClock(`event:${resolvedEventId}`);

const accessToken = computed(() => {
  const token = Array.isArray(route.query.token)
    ? route.query.token[0]
    : route.query.token;
  const normalized = String(token ?? "").trim();
  return normalized || undefined;
});
const tokenFingerprint = computed(() =>
  fingerprintAccessToken(accessToken.value),
);
if (accessToken.value) setPrivateResponseHeaders();

const { data: eventEntityContext, error: eventEntityError } =
  await useAsyncData(
    computed(() => `event-entity-${resolvedEventId}-${tokenFingerprint.value}`),
    async () => {
      const requestToken = accessToken.value;
      try {
        return await $fetch<PublicEventDetailResponse>(
          `/bff/public/events/${resolvedEventId}`,
          {
            query: requestToken ? { token: requestToken } : undefined,
          },
        );
      } catch (error) {
        throwPublicBffError(error, "Event");
      }
    },
  );
if (eventEntityError.value) throw eventEntityError.value;

const resolvedEvent = eventEntityContext.value?.event;
const resolvedEventEstablishment = eventEntityContext.value?.establishment;
if (!resolvedEvent || !resolvedEventEstablishment) {
  setNoStoreResponseHeader();
  throw createError({
    statusCode: 503,
    statusMessage: "Invalid event response",
    fatal: true,
  });
}
const resolvedEventParentId = extractUuidFromSlug(
  String(resolvedEvent.establishmentId ?? ""),
);
if (
  String(resolvedEvent.id).toLowerCase() !== resolvedEventId ||
  !resolvedEvent.name?.trim() ||
  !resolvedEventParentId ||
  String(resolvedEventEstablishment.id).toLowerCase() !==
    resolvedEventParentId ||
  !resolvedEventEstablishment.name?.trim()
) {
  setNoStoreResponseHeader();
  throw createError({
    statusCode: 503,
    statusMessage: "Invalid event response",
    fatal: true,
  });
}
if (!isPublicVenueDetail(resolvedEventEstablishment)) throwEventNotFound();

const resolvedEventIsRestricted = Boolean(eventEntityContext.value?.restricted);
if (resolvedEventIsRestricted) setPrivateResponseHeaders();

const canonicalParentSlug = makeSlug(
  resolvedEventEstablishment.name,
  resolvedEventEstablishment.id,
);
const canonicalEventSlug = makeSlug(resolvedEvent.name, resolvedEvent.id);
const canonicalEventRoute = localePath(
  `/establishment/${canonicalParentSlug}/event/${canonicalEventSlug}`,
);
const needsCanonicalRedirect = route.path !== canonicalEventRoute;
if (needsCanonicalRedirect) {
  await navigateTo(
    {
      path: canonicalEventRoute,
      query: route.query,
      hash: route.hash,
    },
    { redirectCode: 301, replace: true },
  );
}
if (import.meta.server && accessToken.value) {
  useNuxtApp().payload.path = canonicalEventRoute;
}

// Expensive enrichment is skipped for server redirects and runs on canonical pages.
const {
  data: eventData,
  pending: eventPending,
  error: eventError,
} = await useAsyncData(
  computed(() => `event-page-${resolvedEventId}-${tokenFingerprint.value}`),
  async () => {
    const event = resolvedEvent;
    const establishment = resolvedEventEstablishment;
    if (import.meta.server && needsCanonicalRedirect) {
      return {
        event,
        establishment,
        restricted: resolvedEventIsRestricted,
        address: null,
        city: null,
        country: null,
        images: [],
        ticketTypes: [],
        eventServices: [],
        announcements: [],
        categories: [],
      };
    }

    try {
      const imagesPromise = (async () => {
        try {
          const imgData = await eventService.listImagesForEvent(event.id);
          return imgData?.images ?? [];
        } catch (e) {
          console.warn("fetching event images failed", e);
          return [];
        }
      })();

      const ticketTypesPromise = (async () => {
        try {
          const ticketRes = (await eventService.listTicketTypes(1, 50, {
            eventId: event.id,
          })) as { ticketTypes?: unknown[] } | undefined;
          return ticketRes?.ticketTypes ?? [];
        } catch (e) {
          console.warn("fetching ticket types failed", e);
          return [];
        }
      })();

      const eventServicesPromise = (async () => {
        try {
          const svcRes = (await eventService.listEventServices(1, 200, {
            eventId: event.id,
          })) as { eventServices?: unknown[] } | undefined;
          return svcRes?.eventServices ?? [];
        } catch (e) {
          console.warn("fetching event services failed", e);
          return [];
        }
      })();

      const announcementsPromise = (async () => {
        try {
          const annRes = await eventService.listEventAnnouncements(event.id);
          return annRes?.announcements ?? [];
        } catch (e) {
          console.warn("fetching event announcements failed", e);
          return [];
        }
      })();

      const categoriesPromise = (async () => {
        const categoryIds: string[] = event?.categoryIds ?? [];
        const fetched = await Promise.all(
          categoryIds.map(async (id) => {
            try {
              return await eventService.fetchCategory(id);
            } catch (e) {
              console.warn("fetchCategory failed", id, e);
              return null;
            }
          }),
        );
        return fetched.filter((c): c is EventsCategoryResponse => Boolean(c));
      })();

      const [
        imagesRes,
        ticketTypes,
        eventServicesRes,
        announcementsRes,
        categories,
      ] = await Promise.all([
        imagesPromise,
        ticketTypesPromise,
        eventServicesPromise,
        announcementsPromise,
        categoriesPromise,
      ]);

      // Fetch address – prefer the event's own addressId; fall back to the establishment's
      const resolvedAddressId = event?.addressId || establishment?.addressId;

      const address = resolvedAddressId
        ? await addressService.fetchAddress(resolvedAddressId)
        : null;

      const [city, country] = await Promise.all([
        address?.cityId
          ? addressService.fetchCity(address.cityId)
          : Promise.resolve(null),
        address?.countryId
          ? addressService.fetchCountry(address.countryId)
          : Promise.resolve(null),
      ]);

      return {
        event,
        establishment,
        restricted: resolvedEventIsRestricted,
        address,
        city,
        country,
        images: imagesRes,
        ticketTypes,
        eventServices: eventServicesRes,
        announcements: announcementsRes,
        categories,
      };
    } catch (error) {
      setNoStoreResponseHeader();
      throw createError({
        statusCode: 503,
        statusMessage: "Event dependencies unavailable",
        fatal: true,
        cause: error,
      });
    }
  },
);
if (eventError.value) throw eventError.value;

const eventRecord = eventData.value?.event as unknown as Record<
  string,
  unknown
> | null;
const eventAddress = eventData.value?.address as Record<string, unknown> | null;
const eventVenue = eventData.value?.establishment as unknown as Record<
  string,
  unknown
> | null;
const authoritativeEventTimeZone = resolveEventTimeZoneCandidate(
  eventRecord?.timeZone,
  eventRecord?.timezone,
  eventRecord?.time_zone,
  eventData.value?.city?.timezone,
  eventAddress?.timeZone,
  eventAddress?.timezone,
  eventAddress?.time_zone,
  eventVenue?.timeZone,
  eventVenue?.timezone,
  eventVenue?.time_zone,
);
if (!authoritativeEventTimeZone) {
  setNoStoreResponseHeader();
  throw createError({
    statusCode: 503,
    statusMessage: "Invalid event timezone",
    fatal: true,
  });
}

// Computed properties for dynamic data
const evt = computed(() => eventData.value?.event ?? null);
const establishment = computed(() => eventData.value?.establishment ?? null);
const isRestrictedEvent = computed(() => Boolean(eventData.value?.restricted));
type TicketTypeLike = TicketOfferInput & {
  id?: string;
  name?: string;
  ticketName?: string;
  description?: string;
  maxQuantity?: number;
  sold?: number;
  held?: number;
  available?: number | null;
  currency?: string;
  price?: number;
};

type EventServiceOptionLike = {
  id?: string;
  name?: string;
  description?: string;
  price?: number | undefined;
  currency?: string | undefined;
  maxQuantity?: number | undefined;
  sold?: number | undefined;
  held?: number | undefined;
  available?: number | null | undefined;
};

const ticketTypes = computed<TicketTypeLike[]>(() => {
  const value = eventData.value?.ticketTypes;
  return Array.isArray(value) ? (value as TicketTypeLike[]) : [];
});

const eventServices = computed<EventServiceOptionLike[]>(() => {
  const value = eventData.value?.eventServices;
  return Array.isArray(value) ? (value as EventServiceOptionLike[]) : [];
});
const announcements = computed(() => eventData.value?.announcements ?? []);
const announcementCount = computed(() => announcements.value.length);
const showAnnouncements = ref(false);
const categoriesList = computed<EventsCategoryResponse[]>(
  () => eventData.value?.categories ?? [],
);
const estPath = computed(() => canonicalParentSlug);

const eventName = computed(() => {
  return evt.value?.name ?? evt.value?.eventName ?? "";
});
const establishmentName = computed(() => {
  return establishment.value?.name ?? "";
});
const sellerName = computed(() => {
  return establishment.value?.name || "the event organiser";
});
const eventDescription = computed(() => evt.value?.description ?? "");

type EventImageLike =
  | string
  | {
      url?: string | null;
      URL?: string | null;
      Url?: string | null;
      src?: string | null;
      path?: string | null;
      imageUrl?: string | null;
      image_url?: string | null;
      fileUrl?: string | null;
      file_url?: string | null;
      image?: { url?: string | null; URL?: string | null };
      data?: { url?: string | null; URL?: string | null };
      attributes?: { url?: string | null; URL?: string | null };
    };

function extractEventImageUrl(
  img: EventImageLike | null | undefined,
): string | undefined {
  if (!img) return undefined;
  if (typeof img === "string") return img;
  const candidates = [
    img.url,
    img.URL,
    img.Url,
    img.src,
    img.path,
    img.imageUrl,
    img.image_url,
    img.fileUrl,
    img.file_url,
  ];
  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) return candidate;
  }
  if (img.image) {
    const nested = img.image.url ?? img.image.URL;
    if (typeof nested === "string" && nested.trim()) return nested;
  }
  if (img.data) {
    const nested = img.data.url ?? img.data.URL;
    if (typeof nested === "string" && nested.trim()) return nested;
  }
  if (img.attributes) {
    const nested = img.attributes.url ?? img.attributes.URL;
    if (typeof nested === "string" && nested.trim()) return nested;
  }
  return undefined;
}

const imagesList = computed(() => {
  const imgs = eventData.value?.images ?? [];
  const routeQueryImg = route.query?.img ? String(route.query.img) : null;

  // While loading or when no server images yet, show query image first for instant feedback
  if (eventPending.value || imgs.length === 0) {
    if (routeQueryImg) {
      return [routeQueryImg, ...placeholderImages.slice(0, 2)];
    }
    return placeholderImages;
  }

  // Server images loaded - extract and optionally prepend query image
  const urls = imgs
    .map(extractEventImageUrl)
    .filter((url): url is string => Boolean(url));
  if (urls.length === 0) {
    // Server fetch completed but no valid URLs extracted
    if (routeQueryImg) {
      return [routeQueryImg, ...placeholderImages.slice(0, 2)];
    }
    return placeholderImages;
  }

  // We have extracted URLs - prepend query image if present and different
  if (routeQueryImg) {
    const out = [routeQueryImg, ...urls.filter((u) => u !== routeQueryImg)];
    return out;
  }

  return urls;
});

// Date formatting
const intlLocale = computed(() => (locale.value === "de" ? "de-DE" : "en-GB"));
const eventTimeZone = computed(() => authoritativeEventTimeZone);
const timeZoneHint = computed(() =>
  t("event.venueTime", { timeZone: eventTimeZone.value }),
);

function eventDate(raw: string): Date | null {
  return toEventDate(raw, eventTimeZone.value);
}

function formatEventDate(
  raw: string,
  options: Intl.DateTimeFormatOptions,
): string {
  return formatEventDateForZone(raw, eventTimeZone.value, options);
}

function formatEventDateForZone(
  raw: string,
  timeZone: string,
  options: Intl.DateTimeFormatOptions,
): string {
  return (
    formatEventDateInTimeZone(raw, timeZone, intlLocale.value, options) ?? raw
  );
}

function formatLocalizedPrice(amount: number, currencyCode: string): string {
  return formatPrice(amount, currencyCode, intlLocale.value);
}

function formatAnnouncementDate(raw: string): string {
  return formatEventDate(raw, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const startDate = computed(() => {
  const start = evt.value?.start ?? evt.value?.startDate ?? null;
  if (!start) return null;
  try {
    return formatEventDate(start, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  } catch {
    return start;
  }
});

const isMultiDay = computed(() => {
  const startRaw = evt.value?.start ?? evt.value?.startDate ?? null;
  const endRaw = evt.value?.end ?? evt.value?.endDate ?? null;
  if (!startRaw || !endRaw) return false;
  const startKey = eventCalendarDateKey(startRaw, eventTimeZone.value);
  const endKey = eventCalendarDateKey(endRaw, eventTimeZone.value);
  return Boolean(startKey && endKey && startKey !== endKey);
});

function formatDisplayDateTime(raw: string | null | undefined): string {
  if (!raw) return "";
  try {
    const date = formatEventDateForZone(raw, eventTimeZone.value, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    const time = formatEventDateForZone(raw, eventTimeZone.value, {
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    });
    return `${date}, ${time}`;
  } catch {
    return String(raw);
  }
}

const displayFormattedStartDateTime = computed(() => {
  return formatDisplayDateTime(
    evt.value?.start ?? evt.value?.startDate ?? null,
  );
});

const displayFormattedEndDateTime = computed(() => {
  return formatDisplayDateTime(evt.value?.end ?? evt.value?.endDate ?? null);
});

const displayFormattedSingleDate = computed(() => {
  const startRaw = evt.value?.start ?? evt.value?.startDate ?? null;
  if (!startRaw) return t("event.dateTbd");
  try {
    return formatEventDateForZone(startRaw, eventTimeZone.value, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return startDate.value ?? t("event.dateTbd");
  }
});

const displayStartTime = computed(() => {
  const start = evt.value?.start ?? evt.value?.startDate ?? null;
  if (!start) return null;
  try {
    return formatEventDateForZone(start, eventTimeZone.value, {
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    });
  } catch {
    return null;
  }
});

const displayEndTime = computed(() => {
  const end = evt.value?.end ?? evt.value?.endDate ?? null;
  if (!end) return null;
  try {
    return formatEventDateForZone(end, eventTimeZone.value, {
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    });
  } catch {
    return null;
  }
});

const displayTimeRange = computed(() => {
  if (displayStartTime.value && displayEndTime.value) {
    return `${displayStartTime.value} - ${displayEndTime.value}`;
  }
  return displayStartTime.value ?? t("event.timeTbd");
});

const eventScheduleLabel = computed(() => {
  const schedule = isMultiDay.value
    ? `${displayFormattedStartDateTime.value} - ${displayFormattedEndDateTime.value}`
    : `${displayFormattedSingleDate.value}, ${displayTimeRange.value}`;
  return `${schedule} · ${timeZoneHint.value}`;
});

// Event status uses the same IANA timezone conversion as visible dates and JSON-LD.
function toUtcMs(raw: string): number {
  return eventDate(raw)?.getTime() ?? Number.NaN;
}

type EventRecordLike = Record<string, unknown> & {
  id?: string;
  name?: string;
  description?: string;
  price?: number | string;
  currency?: string;
  soldOut?: boolean;
  isCanceled?: boolean;
  is_canceled?: boolean;
  canceled?: boolean;
  cancelled?: boolean;
  status?: string | null;
  minimumAge?: number;
  ageRestrictionEnabled?: boolean;
  latitude?: number | string | null;
  longitude?: number | string | null;
  start?: string | null;
  startDate?: string | null;
  end?: string | null;
  endDate?: string | null;
  previousStartDate?: string | null;
  previousStart?: string | null;
  rescheduledFrom?: string | null;
  ogImageUrl?: string | null;
  og_image_url?: string | null;
  socialImageUrl?: string | null;
  social_image_url?: string | null;
  coverImageUrl?: string | null;
  cover_image_url?: string | null;
  bannerImageUrl?: string | null;
  banner_image_url?: string | null;
  imageUrl?: string | null;
  image_url?: string | null;
};

const eventStatus = computed(
  ():
    | "upcoming"
    | "starting-soon"
    | "in-progress"
    | "past"
    | "canceled"
    | "invalid" => {
    // Check if the event is explicitly canceled by the organiser
    const e = evt.value as EventRecordLike | null | undefined;
    if (e) {
      const canceled =
        e.isCanceled ?? e.is_canceled ?? e.canceled ?? e.cancelled;
      if (canceled === true) return "canceled";
      const status = (e.status ?? "").toLowerCase();
      if (status === "canceled" || status === "cancelled") return "canceled";
    }

    const startRaw = evt.value?.start ?? evt.value?.startDate ?? null;
    const endRaw = evt.value?.end ?? evt.value?.endDate ?? null;
    if (!startRaw) return "invalid";

    const now = eventRenderNow.value;
    const startMs = toUtcMs(startRaw);
    const lastMs = endRaw ? toUtcMs(endRaw) : startMs;
    if (!Number.isFinite(startMs) || (endRaw && !Number.isFinite(lastMs))) {
      return "invalid";
    }

    if (now > lastMs) return "past";
    if (now >= startMs) return "in-progress";
    // "starting soon" = within the next 2 hours
    if (startMs - now <= 2 * 60 * 60 * 1000) return "starting-soon";
    return "upcoming";
  },
);

// Establishment-level gate: the establishment must be enabled (not suspended/disabled by the platform).
// The API may return `isEnabled`, `is_enabled`, or `enabled`; if none of these fields are present
// we assume the establishment is enabled so we don't break existing flows.
const isEstablishmentEnabled = computed(() => {
  const est = establishment.value as Record<string, unknown> | null | undefined;
  if (!est) return true;
  const flag = est.isEnabled ?? est.is_enabled ?? est.enabled;
  if (flag === undefined || flag === null) return true;
  return Boolean(flag);
});

const canPurchase = computed(
  () =>
    eventStatus.value !== "past" &&
    eventStatus.value !== "canceled" &&
    eventStatus.value !== "invalid" &&
    isEstablishmentEnabled.value &&
    !isEventSoldOut.value,
);

const hasAgeRestriction = computed(() => {
  const e = evt.value as EventRecordLike | null | undefined;
  return (
    e?.ageRestrictionEnabled === true &&
    typeof e?.minimumAge === "number" &&
    e.minimumAge > 0
  );
});

type TicketLike = Record<string, unknown> & {
  maxQuantity?: number | null;
  sold?: number | null;
  held?: number | null;
  available?: number | null;
};

/** Whether a specific ticket type is sold out (maxQuantity > 0 and sold >= maxQuantity) */
function isTicketTypeSoldOut(ticket: TicketLike): boolean {
  return isUnavailable(ticket);
}

/** How many tickets remain for a ticket type (null = unlimited) */
function ticketTypeRemaining(ticket: TicketLike): number | null {
  return availableQuantity(ticket);
}

/** True when every ticket type with a cap is sold out */
const isEventSoldOut = computed(() => {
  const types = ticketTypes.value as TicketLike[];
  if (types.length === 0) return false;
  return types.every((ticket) => isTicketTypeSoldOut(ticket));
});

// Price calculation
const lowestTicketOffer = computed(() =>
  getLowestTicketOffer(ticketTypes.value),
);
const lowestPrice = computed(() => lowestTicketOffer.value?.price ?? 0);

const currency = computed(() => {
  if (lowestTicketOffer.value) return lowestTicketOffer.value.currency;
  return evt.value?.currency ?? "EUR";
});

// Location
const lat = ref<number | null>(null);
const lon = ref<number | null>(null);

watchEffect(() => {
  const e = evt.value as unknown as EventRecordLike | null | undefined;
  const a = eventData.value?.address as unknown as
    | Record<string, unknown>
    | null
    | undefined;
  // Prefer event-level coordinates, fall back to the resolved address record
  const maybeLat = e?.latitude ?? a?.latitude ?? null;
  const maybeLon = e?.longitude ?? a?.longitude ?? null;
  lat.value =
    typeof maybeLat === "number"
      ? maybeLat
      : maybeLat
        ? Number(maybeLat)
        : null;
  lon.value =
    typeof maybeLon === "number"
      ? maybeLon
      : maybeLon
        ? Number(maybeLon)
        : null;
});

const formattedAddressLines = computed(() => {
  const a = eventData.value?.address as unknown as
    | Record<string, unknown>
    | null
    | undefined;
  if (!a) return [] as string[];

  const streetParts: string[] = [];
  if ((a as Record<string, unknown>).streetOne)
    streetParts.push(String((a as Record<string, unknown>).streetOne));
  if ((a as Record<string, unknown>).streetTwo)
    streetParts.push(String((a as Record<string, unknown>).streetTwo));
  const streetLine = streetParts.join(" ").trim();

  const cityName = eventData.value?.city?.cityName ?? "";
  const postal = String((a as Record<string, unknown>).postalCode ?? "");
  const cityLine = [postal, cityName].filter(Boolean).join(" ").trim();

  const countryName = eventData.value?.country?.countryName ?? "";

  const lines: string[] = [];
  if (streetLine) lines.push(streetLine);
  if (cityLine) lines.push(cityLine);
  if (countryName) lines.push(countryName);

  return lines;
});

// SEO
const cityName = computed(() => eventData.value?.city?.cityName ?? "");
const countryName = computed(() => eventData.value?.country?.countryName ?? "");

const socialTitle = computed(
  () => `${eventName.value} - ${establishmentName.value}`,
);

const priceLabel = computed(() => {
  if (lowestPrice.value > 0) {
    return t("event.fromPrice", {
      price: formatLocalizedPrice(lowestPrice.value, currency.value),
    });
  }
  if (ticketTypes.value.length > 0) return t("event.free");
  return "";
});

const socialMetaLine = computed(() => {
  const parts = [
    eventScheduleLabel.value,
    cityName.value,
    priceLabel.value,
  ].filter(Boolean);

  return parts.join(" · ");
});

const socialDescriptionSnippet = computed(() => {
  const normalized = normalizeMetaText(eventDescription.value);
  if (!normalized) return "";
  if (normalized.toLowerCase() === "event description not available") return "";
  return normalized.slice(0, 120);
});

const socialMetaDescription = computed(() => {
  const details = socialMetaLine.value;
  const snippet = socialDescriptionSnippet.value;
  const combined = snippet
    ? `${details}${details ? " · " : ""}${snippet}`
    : details;

  return (combined || `${eventName.value} - ${establishmentName.value}`).slice(
    0,
    200,
  );
});

const pageTitle = computed(() => {
  const name = eventName.value;
  const location = cityName.value
    ? t("event.seoLocation", { city: cityName.value })
    : "";
  return t("event.seoTitle", {
    name,
    location,
    establishment: establishmentName.value,
  });
});

const metaDescription = computed(() =>
  socialMetaDescription.value.slice(0, 160),
);

const eventPath = computed(() => {
  return `/establishment/${canonicalParentSlug}/event/${canonicalEventSlug}`;
});

const canonicalPath = computed(() => localePath(eventPath.value));

const canonicalUrl = computed(() => toAbsolutePublicUrl(canonicalPath.value));
const alternateEnUrl = computed(() =>
  toAbsolutePublicUrl(localePath(eventPath.value, "en")),
);
const alternateDeUrl = computed(() =>
  toAbsolutePublicUrl(localePath(eventPath.value, "de")),
);

const seoImageUrls = computed(() => {
  const e = evt.value as EventRecordLike | null | undefined;
  const eventSpecificImages = [
    e?.ogImageUrl,
    e?.og_image_url,
    e?.socialImageUrl,
    e?.social_image_url,
    e?.coverImageUrl,
    e?.cover_image_url,
    e?.bannerImageUrl,
    e?.banner_image_url,
    e?.imageUrl,
    e?.image_url,
  ];
  const galleryImages = (eventData.value?.images ?? []).map(
    extractEventImageUrl,
  );
  const placeholders = new Set(["/images/hero.jpg", SOCIAL_FALLBACK_IMAGE]);

  return [
    ...new Set(
      [...eventSpecificImages, ...galleryImages]
        .filter(
          (image): image is string =>
            typeof image === "string" && Boolean(image.trim()),
        )
        .map((image) => image.trim())
        .filter((image) => !placeholders.has(image) && !/^data:/i.test(image))
        .map(toAbsolutePublicUrl),
    ),
  ];
});

const ogImage = computed(() => {
  return seoImageUrls.value[0] ?? toAbsolutePublicUrl(SOCIAL_FALLBACK_IMAGE);
});

useSeoMeta({
  title: pageTitle,
  description: metaDescription,
  ogTitle: socialTitle,
  ogDescription: socialMetaDescription,
  ogImage: ogImage,
  ogUrl: canonicalUrl,
  ogType: "website",
  ogSiteName: "Upfest",
  twitterCard: "summary_large_image",
  twitterTitle: socialTitle,
  twitterDescription: socialMetaDescription,
  twitterImage: ogImage,
});

useHead(
  computed(() => ({
    link: [
      { rel: "canonical", href: canonicalUrl.value },
      { rel: "alternate", hreflang: "en", href: alternateEnUrl.value },
      { rel: "alternate", hreflang: "de", href: alternateDeUrl.value },
      { rel: "alternate", hreflang: "x-default", href: alternateEnUrl.value },
    ],
    meta: isRestrictedEvent.value
      ? [
          { key: "robots-policy", name: "robots", content: "noindex,nofollow" },
          {
            key: "googlebot-policy",
            name: "googlebot",
            content: "noindex,nofollow",
          },
        ]
      : [],
  })),
);

// Share modal
const isShareModalOpen = ref(false);

const shareLocation = computed(() => {
  const parts = [cityName.value, countryName.value].filter(Boolean);
  return parts.join(", ");
});

const sharePrice = computed(() => {
  if (lowestPrice.value > 0)
    return formatLocalizedPrice(lowestPrice.value, currency.value);
  return "";
});

// JSON-LD Structured Data
const eventSchemaOffer = computed(() => {
  if (
    eventStatus.value === "canceled" ||
    eventStatus.value === "past" ||
    eventStatus.value === "invalid"
  )
    return null;

  if (lowestTicketOffer.value) {
    return {
      ...lowestTicketOffer.value,
      soldOut: isEventSoldOut.value,
    };
  }

  const explicitPrice = Number(
    (evt.value as EventRecordLike | null | undefined)?.price ?? NaN,
  );
  const explicitCurrency = (evt.value as EventRecordLike | null | undefined)
    ?.currency;
  if (
    Number.isFinite(explicitPrice) &&
    explicitPrice >= 0 &&
    typeof explicitCurrency === "string"
  ) {
    return {
      price: explicitPrice,
      currency: explicitCurrency,
      soldOut: Boolean(
        (evt.value as EventRecordLike | null | undefined)?.soldOut,
      ),
    };
  }
  return null;
});

const structuredData = computed(() => {
  const schemas: Record<string, unknown>[] = [];
  if (isRestrictedEvent.value) return schemas;

  if (evt.value) {
    const event = evt.value as unknown as EventRecordLike;
    const address = eventData.value?.address as unknown as
      | Record<string, unknown>
      | null
      | undefined;
    const streetOne = address?.streetOne;
    const streetTwo = address?.streetTwo;
    const postalCode = address?.postalCode;
    const streetAddress = [streetOne, streetTwo].filter(Boolean).join(" ");
    const venueUrl = toAbsolutePublicUrl(
      localePath(`/establishment/${canonicalParentSlug}`),
    );
    const eventSchema = buildEventSchema({
      name: eventName.value,
      canonicalUrl: canonicalUrl.value,
      description: eventDescription.value,
      startDate: event.start ?? event.startDate ?? "",
      endDate: event.end ?? event.endDate,
      timeZone: eventTimeZone.value,
      imageUrls: seoImageUrls.value,
      status: event.status,
      isCanceled: Boolean(
        event.isCanceled ??
        event.is_canceled ??
        event.canceled ??
        event.cancelled,
      ),
      previousStartDate:
        event.previousStartDate ?? event.previousStart ?? event.rescheduledFrom,
      isPrivate: isRestrictedEvent.value,
      venue: {
        name: establishmentName.value,
        canonicalUrl: venueUrl,
        address: {
          streetAddress,
          postalCode: typeof postalCode === "string" ? postalCode : undefined,
          addressLocality: cityName.value,
          addressRegion: eventData.value?.city?.stateProvince,
          addressCountry: countryName.value,
        },
        geo: { latitude: lat.value, longitude: lon.value },
      },
      offer: eventSchemaOffer.value,
      now: eventRenderNow.value,
    });

    if (eventSchema) schemas.push(eventSchema);
  }

  schemas.push(
    buildBreadcrumbSchema([
      { name: t("nav.home"), url: toAbsolutePublicUrl(localePath("/")) },
      {
        name: establishmentName.value,
        url: toAbsolutePublicUrl(
          localePath(`/establishment/${canonicalParentSlug}`),
        ),
      },
      { name: eventName.value, url: canonicalUrl.value },
    ]),
  );

  return schemas;
});

useHead(
  computed(() => ({
    script: structuredData.value.map((schema, index) =>
      jsonLdHeadScript(`event-jsonld-${index}`, schema),
    ),
  })),
);

function currentAbsoluteUrl() {
  if (!import.meta.client) return "";
  const resolved = router.resolve(
    route.fullPath ?? router.currentRoute.value.fullPath ?? "/",
  );
  const href = resolved.href;
  if (href.startsWith("http")) return href;
  return `${window.location.origin}${href}`;
}

function startLoginFlow(_payload?: Record<string, unknown>) {
  if (!import.meta.client) return;
  const redirectTarget = `${currentAbsoluteUrl()}${route.fullPath.includes("?") ? "&" : "?"}buyTickets=1`;
  navigateTo(
    `${localePath("/login")}?redirect=${encodeURIComponent(redirectTarget)}`,
  );
}

const handlePayFromModal = (payload: PurchasePayload) => {
  if (!import.meta.client) return;

  const checkoutPayload = {
    ...payload,
    currency: currency.value,
    eventId: evt.value?.id,
    eventName: eventName.value,
    eventStart: evt.value?.start ?? evt.value?.startDate ?? "",
    eventEnd: evt.value?.end ?? evt.value?.endDate ?? "",
    eventTimeZone: eventTimeZone.value,
    establishmentId: evt.value?.establishmentId,
    establishmentName: establishmentName.value,
    newsletterConsentSeller: payload.newsletterConsentSeller,
    newsletterConsentUpfest: payload.newsletterConsentUpfest,
    establishmentSlug: String(route.params.establishmentSlug ?? ""),
    eventSlug: String(route.params.eventSlug ?? ""),
    accessToken: route.query.token ? String(route.query.token) : "",
    addressId: eventData.value?.address?.id ?? "",
    addressLine: formattedAddressLines.value.join(", "),
    latitude: lat.value ?? 0,
    longitude: lon.value ?? 0,
    serviceOptions: eventServices.value,
    visibility: evt.value?.visibility ?? "",
    privateAccessMode: evt.value?.privateAccessMode ?? "",
    seller: {
      displayName: establishmentName.value,
      legalName: establishmentName.value,
      supportEmail: "",
      registeredAddress: undefined,
      city: cityName.value || undefined,
      country: countryName.value || undefined,
    },
  };

  try {
    localStorage.setItem(
      "upfest-checkout-session",
      JSON.stringify(checkoutPayload),
    );
  } catch (error) {
    console.warn("Failed to persist checkout payload", error);
  }

  emit("checkout_started", checkoutAnalyticsProperties(checkoutPayload));
  router.push(localePath("/checkout"));
};

const onBuyTicketNow = () => {
  if (!canPurchase.value) return;
  isTicketModalOpen.value = true;
};

function closeTicketModal() {
  isTicketModalOpen.value = false;
}

type SoldOutLike = Record<string, unknown> & {
  maxQuantity?: number | null;
  sold?: number | null;
  held?: number | null;
  available?: number | null;
};

function isServiceOptionSoldOut(service: SoldOutLike): boolean {
  return isUnavailable(service);
}

function onTicketTypeClick(ticket: TicketLike) {
  if (!canPurchase.value) return;
  if (isTicketTypeSoldOut(ticket)) return;
  onBuyTicketNow();
}

function onServiceOptionClick(service: SoldOutLike) {
  if (!canPurchase.value) return;
  if (isServiceOptionSoldOut(service)) return;
  onBuyTicketNow();
}

onMounted(() => {
  watch(
    () => evt.value?.id,
    (id) => {
      if (id)
        emit("detail_viewed", {
          content_type: "event",
          event_id: id,
          establishment_id: evt.value?.establishmentId,
          has_tickets: ticketTypes.value.length > 0,
        });
    },
    { immediate: true },
  );
  if (route.query.buyTickets === "1" && canPurchase.value) {
    isTicketModalOpen.value = true;
  }

  // Track that the event page was opened
  const eventId = evt.value?.id;
  if (eventId) {
    trackOpened("event", eventId);
  }

  // Also track that the parent establishment was viewed via this event
  const estId = establishment.value?.id;
  if (estId) {
    trackOpened("establishment", estId);
  }
});

watch(isTicketModalOpen, (open, previous) => {
  if (open && !previous)
    emit("ticket_purchase_started", {
      event_id: evt.value?.id,
      establishment_id: evt.value?.establishmentId,
      currency: currency.value,
    });
});

// If data loads after mount (SSR hydration), fire the opened event then
watch(
  () => evt.value?.id,
  (id) => {
    if (id) trackOpened("event", id);
  },
  { once: true },
);
</script>

<style scoped>
/* Make tiles grayscale like MapPicker */
:deep(.leaflet-tile) {
  filter: grayscale(100%) contrast(0.95) brightness(0.95);
}

/* Custom pin styles */
:deep(.custom-pin) {
  display: inline-block;
  line-height: 0;
}

:deep(.custom-pin svg) {
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.18));
}

/* Ensure map container fills area */
.leaflet-container {
  height: 100%;
  width: 100%;
}

/* Vertical text for sidebar ad placeholders */
.writing-vertical {
  writing-mode: vertical-rl;
  text-orientation: mixed;
}

.option-row {
  width: 100%;
  border-radius: 0.75rem;
  padding: 0.625rem;
  border: 1px solid transparent;
  box-sizing: border-box;
}

.option-row--interactive {
  cursor: pointer;
  transition:
    background-color 140ms ease,
    border-color 140ms ease,
    transform 100ms ease,
    box-shadow 140ms ease;
}

.option-row--interactive:hover {
  background: #f5f3ff;
  border-color: #ddd6fe;
  box-shadow: 0 1px 2px rgba(46, 35, 75, 0.08);
}

.option-row--interactive:active {
  transform: scale(0.992);
  background: #ede9fe;
  border-color: #c4b5fd;
}
</style>
